import { useCallback } from 'react';

import { useChartContext } from '@/components/Chart/context/ChartContext';
import type { ExtendedSeriesType } from '@/components/Chart/types/highcharts';
import getUnitSuffix from '@/components/Chart/utils/getUnitSuffix';
import useDateText, { DateTextVariants } from '@/components/DateText/useDateText';

import type { ExportingMimeTypeValue } from 'highcharts';

const getSeriesNamesWithUnits = (series: ExtendedSeriesType[]) =>
  series.map(s => ({ name: `${s.name}${getUnitSuffix(s.userOptions.custom)}` }));

const useChartImageExport = () => {
  const { formatDateString } = useDateText();
  const { chartRef } = useChartContext();

  const getDateSuffix = useCallback(() => {
    const date = formatDateString(new Date().toJSON(), DateTextVariants.WITH_TIME);
    return date.split(' ').join('T');
  }, [formatDateString]);

  const onExport = useCallback(
    (type: ExportingMimeTypeValue) => {
      const chart = chartRef.current?.chart;
      if (!chart) return;

      const baseOptions = chart.options.exporting?.chartOptions ?? chart.options;

      chart.exportChart(
        { type, filename: `GX-Chart-${getDateSuffix()}` },
        {
          ...baseOptions,
          series: getSeriesNamesWithUnits(chart.series as ExtendedSeriesType[]),
          // Pass export type so exportEvents.load() can branch on it
          exporting: { type }
        }
      );
    },
    [chartRef, getDateSuffix]
  );

  return { onExport };
};

export default useChartImageExport;
